import React, { useState, useCallback, useContext, useEffect } from 'react';
import { useGetHospitalProcedureInsightsQuery } from '../../api/procedureSlice';
import {
    Stack, Paper, Table, TableBody, TableCell, TableContainer,
    TableRow, Checkbox, TextField, InputAdornment, Button,
} from '@mui/material';
import { Search, OpenInNew } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { UserContext } from '../Hooks/useUser';
import MultiProcedureSpendChart from './MultiProcedureSpendChart';
import { useTableSort, sortData } from '../../utils/tableSorting';
import { formatCurrency } from '../../utils/formatNumbers';
import { theme } from '../../theme';
import SortableTableHeader from '../CustomComponents/SortableTableHeader';

function ProcedureInsights() {
    const { user } = useContext(UserContext);
    const [selectedProcedures, setSelectedProcedures] = useState([]);
    const [search, setSearch] = useState('');
    const { order, orderBy, handleRequestSort } = useTableSort('cv_case_cost', 'desc');
    const hospitalId = user.hospital.id;

    const toggleProcedure = useCallback((procedure) => {
        if (!procedure?.id) return;
        setSelectedProcedures(prev => {
            const exists = prev.some(p => p.id === procedure.id);
            return exists ? prev.filter(p => p.id !== procedure.id) : [...prev, procedure];
        });
    }, []);

    const { data: procedures = [], isLoading } = useGetHospitalProcedureInsightsQuery(hospitalId);

    const filteredProcedures = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return procedures;
        return procedures.filter(p =>
            p.name?.toLowerCase().includes(q) || p.external_id?.toLowerCase().includes(q)
        );
    }, [procedures, search]); 

    const sortedProcedures = React.useMemo(() => sortData(filteredProcedures, order, orderBy), [filteredProcedures, order, orderBy]);

    const fmtPct = (v) => `${(v * 100).toFixed(1)}%`;
    const openProcedureAnalysis = (id) => window.open(`/procedures/${id}/analysis`, '_blank', 'noopener,noreferrer');

    useEffect(() => {
        // reset selected procedures to close the chart tab
        setSelectedProcedures([]);
    }, [hospitalId]);


    const someSelected = sortedProcedures.some(p => selectedProcedures.some(s => s.id === p.id));
    
    const handleSelectAll = () => {
        setSelectedProcedures([]);
    };
    
    const headers = [
        { id: 'name', label: 'Procedure', sortable: true, tooltip: '' },
        { id: 'external_id', label: 'ID', sortable: true, tooltip: '' },
        { id: 'average_case_cost', label: 'Avg Case Cost', sortable: true, tooltip: '' },
        { id: 'cv_case_cost', label: 'Case Cost Var.', sortable: true, tooltip: '' },
        { id: 'num_cases', label: '# Cases', sortable: true, tooltip: '' },
        { id: 'average_card_cost', label: 'Avg Card Cost', sortable: true, tooltip: '' },
        { id: 'cv_card_cost', label: 'Card Cost Var.', sortable: true, tooltip: '' },
        { id: 'num_cards', label: '# Cards', sortable: true, tooltip: '' },
        { id: 'details', label: 'Analysis', sortable: false, tooltip: '', justify: 'center' },
    ];

    const checkboxCell = (
        <TableCell padding='checkbox'>
            <Checkbox
                size='small'
                checked={false}
                indeterminate={someSelected}
                onChange={handleSelectAll}
            />
        </TableCell>
    );

    const renderCell = (proc, key) => {
        if (key === 'average_case_cost') return formatCurrency(proc.average_case_cost);
        if (key === 'average_card_cost') return formatCurrency(proc.average_card_cost);
        if (key === 'cv_case_cost') return fmtPct(proc.cv_case_cost);
        if (key === 'cv_card_cost') return fmtPct(proc.cv_card_cost);
        return proc[key];
    };

    return (
        <Stack spacing={2} pb={3}>
            <Stack direction='row' px={3} pb={2} spacing={4} sx={{ backgroundColor: theme.palette.background.white, borderBottom: 1, borderColor: 'divider', justifyContent: 'space-between', alignItems: 'center'}}>
                <Stack spacing={0.5}>
                    <h2>Procedure Insights</h2>
                    <h5>{user.hospital.name}</h5>    
                </Stack>
                <TextField
                    placeholder='Search by name or ID'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    size='small'
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <Search />
                            </InputAdornment>
                        )
                    }}
                />
            </Stack>
            <Stack spacing={2} px={3}>
                
                <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, maxHeight: '40vh' }}>
                    <Table stickyHeader size='small'>
                        <SortableTableHeader
                            headers={headers}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            leadingCell={checkboxCell}
                        />
                        <TableBody>
                            {isLoading && (
                                <TableRow>
                                    <TableCell colSpan={headers.length} align='center'>Loading...</TableCell>
                                </TableRow>
                            )}
                            {sortedProcedures.map(proc => {
                                const isSelected = selectedProcedures.some(p => p.id === proc.id);
                                return (
                                    <TableRow
                                        key={proc.id}
                                        hover
                                        selected={isSelected}
                                        onClick={() => toggleProcedure(proc)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell padding='checkbox'>
                                            <Checkbox
                                                checked={isSelected}
                                                size='small'
                                                onClick={e => e.stopPropagation()}
                                                onChange={() => toggleProcedure(proc)}
                                            />
                                        </TableCell>
                                        {headers.map(col => (
                                            col.id === 'details'
                                                ? (
                                                    <TableCell key='details' align='center' onClick={e => e.stopPropagation()}>
                                                        <Tooltip title='Open in new tab'>
                                                            <Button
                                                                size='small'
                                                                color='primary'
                                                                onClick={() => openProcedureAnalysis(proc.id)}
                                                                startIcon={<OpenInNew fontSize='inherit' />}
                                                                sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                                                            >
                                                                Details
                                                            </Button>
                                                        </Tooltip>
                                                    </TableCell>
                                                )
                                                : (
                                                    <TableCell key={col.id}>
                                                        {renderCell(proc, col.id)}
                                                    </TableCell>
                                                )
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {selectedProcedures.length > 0 && (
                    <Stack spacing={2}>
                        <MultiProcedureSpendChart
                            selectedProcedures={selectedProcedures}
                            onViewDetails={(id) => openProcedureAnalysis(id)}
                        />
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
}

export default ProcedureInsights;